import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getFacilityWithPropuestas } from '@/api/clientes';
import { Header } from '@/components/Header';
import styles from './pages.module.css';
import listStyles from '../components/facility-tabs.module.css';

const ESTADO_LABEL: Record<string, { label: string; color: string }> = {
  borrador: { label: 'Borrador', color: '#6b7280' },
  adjudicada: { label: 'Adjudicada', color: '#2563eb' },
  en_implementacion: { label: 'En implementación', color: '#d97706' },
  entregada: { label: 'Entregada', color: '#059669' },
  cancelada: { label: 'Cancelada', color: '#b91c1c' },
};

export function FacilityPage() {
  const { clienteSlug, facilitySlug } = useParams<{
    clienteSlug: string;
    facilitySlug: string;
  }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['facility-with-propuestas', clienteSlug, facilitySlug],
    queryFn: () => getFacilityWithPropuestas(clienteSlug!, facilitySlug!),
    enabled: !!clienteSlug && !!facilitySlug,
  });

  if (isLoading) {
    return (
      <>
        <Header breadcrumbs={[{ label: 'Inicio', to: '/' }]} title="Cargando…" />
        <div className={`container ${styles.body}`}>
          <div className="empty">Cargando…</div>
        </div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <Header breadcrumbs={[{ label: 'Inicio', to: '/' }]} title="No encontrada" />
        <div className={`container ${styles.body}`}>
          <div className="empty">No se pudo cargar la facility.</div>
        </div>
      </>
    );
  }

  const { cliente, facility, propuestas } = data;
  const facHitos = facility.progress?.hitos;
  const facComps = facility.progress?.compromisos;

  // Hex → rgba util (para el track tintado con el color del cliente)
  function hexA(hex: string, a: number) {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  const trackBg = hexA(cliente.color, 0.15);
  const trackBorder = hexA(cliente.color, 0.25);

  return (
    <>
      <Header
        breadcrumbs={[
          { label: 'Inicio', to: '/' },
          { label: cliente.nombre, to: `/clientes/${cliente.slug}` },
          { label: facility.nombre },
        ]}
        title={facility.nombre}
        subtitle={facility.subtitulo}
        meta={
          <>
            <span>{propuestas.length} propuestas adjudicadas</span>
          </>
        }
      />
      <div className={`container ${styles.body}`}>
        {propuestas.length === 0 ? (
          <div className="placeholder-wrap">
            <div className="icon">📄</div>
            <h3>Aún no hay propuestas cargadas para {facility.nombre}</h3>
            <p>{facility.nota || 'Cuando se cargue la primera propuesta aparecerá aquí.'}</p>
          </div>
        ) : (
          <div className={listStyles.bpFase}>
            {/* Header oscuro con título + barras globales de la facility */}
            <div
              className={listStyles.bpFaseHeader}
              style={{ background: cliente.color, paddingTop: 10, paddingBottom: 10 }}
            >
              <div className={listStyles.bpFaseTitle}>
                <span style={{ fontSize: 20 }}>📋</span>
                <span>Listado de Propuestas</span>
              </div>
              {facility.progress && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    minWidth: 280,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'white',
                    }}
                  >
                    <span style={{ minWidth: 80, opacity: 0.85 }}>Hitos</span>
                    <span style={{ opacity: 0.9, fontWeight: 600 }}>
                      {facHitos!.done} / {facHitos!.total}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: 999,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${facHitos!.pct}%`,
                          height: '100%',
                          background: 'white',
                          borderRadius: 999,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
                      {facHitos!.pct}%
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'white',
                    }}
                  >
                    <span style={{ minWidth: 80, opacity: 0.85 }}>Compromisos</span>
                    <span style={{ opacity: 0.9, fontWeight: 600 }}>
                      {facComps!.done} / {facComps!.total}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: 999,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${facComps!.pct}%`,
                          height: '100%',
                          background: 'rgba(255,255,255,0.7)',
                          borderRadius: 999,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
                      {facComps!.pct}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Una fila por propuesta */}
            <div>
              {propuestas.map((p) => {
                const est = ESTADO_LABEL[p.estado] || { label: p.estado, color: '#6b7280' };
                const healthClass =
                  p.progress?.health === 'red'
                    ? listStyles.bpHitoCompactHealthRed
                    : p.progress?.health === 'yellow'
                      ? listStyles.bpHitoCompactHealthYellow
                      : p.progress?.health === 'green'
                        ? listStyles.bpHitoCompactHealthGreen
                        : '';
                return (
                  <div
                    key={p.id}
                    className={`${listStyles.bpHitoCompact} ${healthClass}`}
                    style={{ cursor: 'pointer', paddingTop: 12, paddingBottom: 12 }}
                    onClick={() => navigate(`/clientes/${cliente.slug}/${facility.slug}/${p.id}`)}
                  >
                    <span
                      className={listStyles.bpDot}
                      style={{ background: est.color }}
                      title={est.label}
                    />
                    <div className={listStyles.bpCompactBody}>
                      <div className={listStyles.bpLine1}>
                        <div className={listStyles.bpLine1Left}>
                          <span className={listStyles.bpLine1Title}>
                            {p.codigo} · {p.nombre}
                          </span>
                          <span className={listStyles.bpLine1Sep}>·</span>
                          <span
                            style={{
                              background: est.color + '22',
                              color: est.color,
                              fontSize: 10,
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '.04em',
                            }}
                          >
                            {est.label}
                          </span>
                          {p.fechaAdjudicacion && (
                            <span className={listStyles.bpLine1Parent}>
                              Adjudicada {p.fechaAdjudicacion}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            minWidth: 320,
                            marginRight: 24,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                          }}
                        >
                          {p.progress ? (
                            <>
                              {/* Hitos */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  fontSize: 11,
                                  color: 'var(--text)',
                                }}
                              >
                                <span style={{ minWidth: 80, color: 'var(--text-soft)' }}>
                                  Hitos
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  {p.progress.hitos.done} / {p.progress.hitos.total}
                                </span>
                                <div
                                  style={{
                                    flex: 1,
                                    height: 6,
                                    background: trackBg,
                                    border: `1px solid ${trackBorder}`,
                                    borderRadius: 999,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${p.progress.hitos.pct}%`,
                                      height: '100%',
                                      background: cliente.color,
                                      borderRadius: 999,
                                      transition: 'width 0.4s ease',
                                    }}
                                  />
                                </div>
                                <span style={{ fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
                                  {p.progress.hitos.pct}%
                                </span>
                              </div>
                              {/* Compromisos */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  fontSize: 11,
                                  color: 'var(--text)',
                                }}
                              >
                                <span style={{ minWidth: 80, color: 'var(--text-soft)' }}>
                                  Compromisos
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  {p.progress.compromisos.done} / {p.progress.compromisos.total}
                                </span>
                                <div
                                  style={{
                                    flex: 1,
                                    height: 6,
                                    background: trackBg,
                                    border: `1px solid ${trackBorder}`,
                                    borderRadius: 999,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${p.progress.compromisos.pct}%`,
                                      height: '100%',
                                      background: cliente.color,
                                      opacity: 0.55,
                                      borderRadius: 999,
                                      transition: 'width 0.4s ease',
                                    }}
                                  />
                                </div>
                                <span style={{ fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
                                  {p.progress.compromisos.pct}%
                                </span>
                              </div>
                            </>
                          ) : (
                            <span
                              style={{
                                fontSize: 11,
                                color: 'var(--text-soft)',
                                fontStyle: 'italic',
                              }}
                            >
                              sin datos de progreso
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
